/**
 * "Founding Partner 프로필" — 업체별 수신 가능 Topic/카테고리/지역/언어/자격/신규
 * 문의 가능 여부를 구조화한다 (P2.5, docs/OZK_FINAL_LAUNCH_STRATEGY.md 15장).
 *
 * This only *structures* the partner list - it does not implement the
 * matching algorithm (that is P3, docs/OZK_FINAL_LAUNCH_STRATEGY.md 11장).
 * Setting partnerStatus to "founding" or "premium" here is what grants a
 * business inquiryManagement for free even without a paid subscription -
 * see functions/utils/business-entitlements.ts's applyFoundingPartnerGrant.
 */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Handshake, Plus, Search, XCircle } from "lucide-react";
import { apiGet, apiPatch, apiPost } from "./api";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

type PartnerStatus = "candidate" | "invited" | "founding" | "premium" | "paused" | "removed";
type CredentialStatus = "unverified" | "submitted" | "verified" | "rejected";

type PartnerProfile = {
  id: number;
  businessId: string;
  businessName: string | null;
  businessNameKo: string | null;
  partnerStatus: PartnerStatus;
  acceptedTopicIds: string[];
  acceptedCategories: string[];
  serviceAreas: string[];
  languages: string[];
  consultationMethods: string[];
  acceptsNewLeads: boolean;
  maxDailyLeads: number | null;
  responseSlaHours: number | null;
  credentialStatus: CredentialStatus;
  active: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

type BusinessOption = {
  id: string;
  businessName: string;
  businessNameKo: string | null;
  suburb: string | null;
  state: string | null;
};

const statusOptions: { value: PartnerStatus | "all"; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "candidate", label: "후보" },
  { value: "invited", label: "초대됨" },
  { value: "founding", label: "Founding Partner" },
  { value: "premium", label: "Premium" },
  { value: "paused", label: "일시중지" },
  { value: "removed", label: "제외됨" },
];
const statusLabel: Record<string, string> = Object.fromEntries(statusOptions.map((o) => [o.value, o.label]));

const credentialOptions: { value: CredentialStatus; label: string }[] = [
  { value: "unverified", label: "미확인" },
  { value: "submitted", label: "제출됨" },
  { value: "verified", label: "확인됨" },
  { value: "rejected", label: "반려" },
];

function parseCommaList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function BusinessPartnerProfiles() {
  const { toast } = useToast();
  const [status, setStatus] = useState<string>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [businessQuery, setBusinessQuery] = useState("");
  const [businessResults, setBusinessResults] = useState<BusinessOption[]>([]);
  const [businessSearching, setBusinessSearching] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessOption | null>(null);
  const [detailTarget, setDetailTarget] = useState<PartnerProfile | null>(null);
  const [detailDraft, setDetailDraft] = useState<{
    acceptedTopicIds: string;
    acceptedCategories: string;
    serviceAreas: string;
    languages: string;
    consultationMethods: string;
    maxDailyLeads: string;
    responseSlaHours: string;
    notes: string;
  } | null>(null);

  const list = useQuery({
    queryKey: ["admin-business-partner-profiles", status],
    queryFn: () =>
      apiGet<{ ok: true; items: PartnerProfile[] }>(
        `/api/business-partner-profiles${status !== "all" ? `?status=${status}` : ""}`,
      ),
  });

  const items = list.data?.items || [];

  const createMutation = useMutation({
    mutationFn: (businessId: string) => apiPost("/api/business-partner-profiles", { businessId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-business-partner-profiles"] });
      toast({ title: "파트너 프로필을 만들었습니다" });
      closeCreateDialog();
    },
    onError: (error: Error) => toast({ title: "생성 실패", description: error.message, variant: "destructive" }),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      apiPatch(`/api/business-partner-profiles/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-business-partner-profiles"] });
      toast({ title: "프로필을 갱신했습니다" });
    },
    onError: (error: Error) => toast({ title: "갱신 실패", description: error.message, variant: "destructive" }),
  });

  const closeCreateDialog = () => {
    setCreateOpen(false);
    setBusinessQuery("");
    setBusinessResults([]);
    setSelectedBusiness(null);
  };

  const searchBusinesses = async () => {
    if (!businessQuery.trim()) return;
    setBusinessSearching(true);
    try {
      const results = await apiGet<BusinessOption[]>(
        `/api/business-directory?q=${encodeURIComponent(businessQuery.trim())}&limit=10`,
      );
      setBusinessResults(Array.isArray(results) ? results : []);
    } catch (error: any) {
      toast({ title: "업체 검색 실패", description: error?.message, variant: "destructive" });
    } finally {
      setBusinessSearching(false);
    }
  };

  const openDetailDialog = (profile: PartnerProfile) => {
    setDetailTarget(profile);
    setDetailDraft({
      acceptedTopicIds: profile.acceptedTopicIds.join(", "),
      acceptedCategories: profile.acceptedCategories.join(", "),
      serviceAreas: profile.serviceAreas.join(", "),
      languages: profile.languages.join(", "),
      consultationMethods: profile.consultationMethods.join(", "),
      maxDailyLeads: profile.maxDailyLeads !== null ? String(profile.maxDailyLeads) : "",
      responseSlaHours: profile.responseSlaHours !== null ? String(profile.responseSlaHours) : "",
      notes: profile.notes || "",
    });
  };
  const closeDetailDialog = () => {
    setDetailTarget(null);
    setDetailDraft(null);
  };

  const saveDetail = () => {
    if (!detailTarget || !detailDraft) return;
    patch.mutate({
      id: detailTarget.id,
      body: {
        acceptedTopicIds: parseCommaList(detailDraft.acceptedTopicIds),
        acceptedCategories: parseCommaList(detailDraft.acceptedCategories),
        serviceAreas: parseCommaList(detailDraft.serviceAreas),
        languages: parseCommaList(detailDraft.languages),
        consultationMethods: parseCommaList(detailDraft.consultationMethods),
        maxDailyLeads: detailDraft.maxDailyLeads.trim() ? Number(detailDraft.maxDailyLeads) : null,
        responseSlaHours: detailDraft.responseSlaHours.trim() ? Number(detailDraft.responseSlaHours) : null,
        notes: detailDraft.notes.trim() || null,
      },
    });
    closeDetailDialog();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Founding Partner 프로필</h2>
          <p className="text-sm text-muted-foreground">
            선정 업체별 수신 가능 Topic·지역·언어·자격·문의 한도를 관리합니다. Founding Partner/Premium 상태인 업체는 유료 구독 없이도 문의 관리 권한을 받습니다.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />프로필 추가
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Handshake className="h-4 w-4" />파트너 목록 ({items.length}건)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              {statusOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>업체</TableHead>
                  <TableHead>Topic / 카테고리</TableHead>
                  <TableHead>지역 / 언어</TableHead>
                  <TableHead>신규 문의</TableHead>
                  <TableHead>자격확인</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((profile) => (
                  <TableRow key={profile.id}>
                    <TableCell>
                      <div className="font-medium">{profile.businessNameKo || profile.businessName}</div>
                      <div className="text-xs text-muted-foreground">{profile.businessId}</div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="flex flex-wrap gap-1">
                        {profile.acceptedTopicIds.slice(0, 3).map((id) => (
                          <Badge key={id} variant="secondary" className="text-xs">{id}</Badge>
                        ))}
                        {profile.acceptedTopicIds.length === 0 && profile.acceptedCategories.length === 0 && (
                          <span className="text-xs text-muted-foreground">미설정</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {profile.serviceAreas.join(", ") || "-"}
                      <br />
                      {profile.languages.join(", ") || "-"}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={profile.acceptsNewLeads}
                        onCheckedChange={(checked) =>
                          patch.mutate({ id: profile.id, body: { acceptsNewLeads: checked } })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={profile.credentialStatus}
                        onValueChange={(value) => patch.mutate({ id: profile.id, body: { credentialStatus: value } })}
                      >
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {credentialOptions.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={profile.partnerStatus}
                        onValueChange={(value) => patch.mutate({ id: profile.id, body: { partnerStatus: value } })}
                      >
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {statusOptions.filter((o) => o.value !== "all").map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openDetailDialog(profile)}>
                        범위 편집
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!list.isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                      해당 상태의 파트너 프로필이 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={(open) => { if (!open) closeCreateDialog(); }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>파트너 프로필 추가</DialogTitle>
            <DialogDescription>
              업체를 검색해 선택하면 "후보" 상태로 프로필이 만들어집니다. Topic·지역·언어 등 세부 범위는 만든 뒤 "범위 편집"에서 채웁니다.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={businessQuery}
                onChange={(e) => setBusinessQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") searchBusinesses(); }}
                placeholder="업체명으로 검색"
              />
              <Button type="button" variant="outline" onClick={searchBusinesses} disabled={businessSearching}>
                <Search className="mr-1 h-4 w-4" />검색
              </Button>
            </div>

            {businessResults.length > 0 && (
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-1">
                {businessResults.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setSelectedBusiness(b)}
                    className={`w-full rounded p-2 text-left text-sm hover:bg-muted ${selectedBusiness?.id === b.id ? "bg-muted" : ""}`}
                  >
                    <div className="font-medium">{b.businessNameKo || b.businessName}</div>
                    <div className="text-xs text-muted-foreground">{b.suburb || ""} {b.state || ""}</div>
                  </button>
                ))}
              </div>
            )}

            {selectedBusiness && (
              <div className="rounded-md border bg-muted/40 p-3 text-sm">
                선택된 업체: <span className="font-medium">{selectedBusiness.businessNameKo || selectedBusiness.businessName}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeCreateDialog}>
              <XCircle className="mr-1 h-4 w-4" />취소
            </Button>
            <Button
              type="button"
              disabled={!selectedBusiness || createMutation.isPending}
              onClick={() => selectedBusiness && createMutation.mutate(selectedBusiness.id)}
            >
              <Plus className="mr-1 h-4 w-4" />프로필 만들기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailTarget} onOpenChange={(open) => { if (!open) closeDetailDialog(); }}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>범위 편집</DialogTitle>
            <DialogDescription>
              {detailTarget?.businessNameKo || detailTarget?.businessName} — 콤마(,)로 여러 값을 구분해 입력하세요. 매칭 알고리즘 자체는 아직 없고(P3), 여기서 정한 범위는 향후 매칭의 필수 조건으로 쓰입니다.
            </DialogDescription>
          </DialogHeader>

          {detailDraft && (
            <div className="grid gap-3">
              <div className="space-y-1">
                <Label>수신 가능 Topic id</Label>
                <Textarea
                  rows={2}
                  value={detailDraft.acceptedTopicIds}
                  onChange={(e) => setDetailDraft({ ...detailDraft, acceptedTopicIds: e.target.value })}
                  placeholder="예: rental-bond-disputes, eviction-termination"
                />
              </div>
              <div className="space-y-1">
                <Label>수신 가능 업종 카테고리</Label>
                <Input
                  value={detailDraft.acceptedCategories}
                  onChange={(e) => setDetailDraft({ ...detailDraft, acceptedCategories: e.target.value })}
                  placeholder="예: realestate, legal"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>서비스 지역</Label>
                  <Input
                    value={detailDraft.serviceAreas}
                    onChange={(e) => setDetailDraft({ ...detailDraft, serviceAreas: e.target.value })}
                    placeholder="예: Sydney CBD, Strathfield"
                  />
                </div>
                <div className="space-y-1">
                  <Label>상담 가능 언어</Label>
                  <Input
                    value={detailDraft.languages}
                    onChange={(e) => setDetailDraft({ ...detailDraft, languages: e.target.value })}
                    placeholder="예: ko, en"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>상담 방식</Label>
                  <Input
                    value={detailDraft.consultationMethods}
                    onChange={(e) => setDetailDraft({ ...detailDraft, consultationMethods: e.target.value })}
                    placeholder="phone, kakao"
                  />
                </div>
                <div className="space-y-1">
                  <Label>하루 문의 한도</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={detailDraft.maxDailyLeads}
                    onChange={(e) => setDetailDraft({ ...detailDraft, maxDailyLeads: e.target.value })}
                    placeholder="비우면 제한 없음"
                  />
                </div>
                <div className="space-y-1">
                  <Label>응답 SLA (시간)</Label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={detailDraft.responseSlaHours}
                    onChange={(e) => setDetailDraft({ ...detailDraft, responseSlaHours: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>관리자 메모 (내부용, 업체·고객에게 노출 안 됨)</Label>
                <Textarea
                  rows={2}
                  value={detailDraft.notes}
                  onChange={(e) => setDetailDraft({ ...detailDraft, notes: e.target.value })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeDetailDialog}>
              <XCircle className="mr-1 h-4 w-4" />취소
            </Button>
            <Button type="button" onClick={saveDetail} disabled={patch.isPending}>
              저장
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
